import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { changeSourceSchema, revisionReasonSchema } from '../enums/change-source.enum.js'

/**
 * A copy of a plan the database kept: what it held and what became of it.
 *
 * Not the pieces themselves — a list of these has to stay small, and the pieces
 * are only wanted when one is restored, which the server does from its own copy.
 * `pieces` and `piecesAfter` are what a person reads to tell a small tidy from a
 * plan that lost nearly everything.
 */
export const planRevisionDtoSchema = z.object({
  id: z.string().uuid(),
  /** When the change that this copy preceded was made. */
  at: instantSchema,
  reason: revisionReasonSchema,
  /** Which path made the change; `unknown` when nothing said. */
  source: changeSourceSchema,
  /**
   * Who made it, by name. Empty when nobody is recorded; a person who has since
   * been deleted is named as a former user rather than dropped.
   */
  actors: z.array(z.string()),
  /** The plan the copy is a copy of. */
  phaseLabel: z.string(),
  /** Pieces the plan held before the change. */
  pieces: z.number().int().nonnegative(),
  /** Pieces it held after. Zero for a plan that was deleted. */
  piecesAfter: z.number().int().nonnegative(),
})

export type PlanRevisionDto = z.infer<typeof planRevisionDtoSchema>

export const planRevisionListDtoSchema = z.object({
  revisions: z.array(planRevisionDtoSchema),
  /**
   * Whether a copy can be put back now. False for a plan that is confirmed, past
   * or canceled: it is settled, and restoring would edit it. Always true for the
   * list of deleted plans, where the checks that can refuse happen on restore.
   */
  restorable: z.boolean(),
})

export type PlanRevisionListDto = z.infer<typeof planRevisionListDtoSchema>
